import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IIncompleteApplicant, IIncompleteApplicantsState } from 'Components/Applicants/ApplicantsModel';
import { AxiosResponse } from 'axios';
import httpAdapterInstance from 'configs/HttpAdapterConfig';
import { exportToCSV } from 'helpers/fileHandlers';
import { IBaseResponse } from 'shared/SharedModels';
import { ENDPOINTS } from 'shared/constants';

const initialState: IIncompleteApplicantsState = {
    getIncompleteApplicantsListStatus: 'idle',
    incompleteApplicantsList: []
};

export const getIncompleteApplicantsList = createAsyncThunk<IBaseResponse<IIncompleteApplicant[]>, { jobId: number }, { rejectValue: IBaseResponse }>(
    'getIncompleteApplicantsList',
    async ({ jobId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .get(`${ENDPOINTS.APPLICANT_INCOMPLETE}/list?jobId=${jobId}`)
            .then((response: AxiosResponse<IBaseResponse<IIncompleteApplicant[]>>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const exportIncompleteApplicantsListAsCSV = createAsyncThunk<IBaseResponse, { jobId: number }, { rejectValue: IBaseResponse }>(
    'exportIncompleteApplicantsListAsCSV',
    async ({ jobId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .get(`${ENDPOINTS.APPLICANT_INCOMPLETE}/export?jobId=${jobId}`)
            .then((response: AxiosResponse<IBaseResponse>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

const incompleteApplicantsSlice = createSlice({
    name: 'incompleteApplicants',
    initialState: initialState,
    reducers: {
        resetIncompleteApplicants: () => initialState,
        resetExportApplicantsAsCSV: (state) => {
            state.downloadAsCSVStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        // get incomplete applicants list.
        builder.addCase(getIncompleteApplicantsList.pending, (state) => {
            state.getIncompleteApplicantsListStatus = 'pending';
        });
        builder.addCase(getIncompleteApplicantsList.fulfilled, (state, action) => {
            state.getIncompleteApplicantsListStatus = 'success';
            state.getIncompleteApplicantsListResponse = action?.payload.message;
            state.incompleteApplicantsList = action.payload?.data;
        });
        builder.addCase(getIncompleteApplicantsList.rejected, (state, action) => {
            state.getIncompleteApplicantsListStatus = 'failed';
            state.getIncompleteApplicantsListResponse = action?.payload?.message ?? 'Error occurred while sharing to Email';
        });
        // download as csv
        builder.addCase(exportIncompleteApplicantsListAsCSV.pending, (state) => {
            state.downloadAsCSVStatus = 'pending';
        });
        builder.addCase(exportIncompleteApplicantsListAsCSV.fulfilled, (state, action) => {
            state.downloadAsCSVStatus = 'success';
            // Download file handler.
            exportToCSV(action?.payload, 'Incomplete Applicants');
        });
        builder.addCase(exportIncompleteApplicantsListAsCSV.rejected, (state, action) => {
            state.downloadAsCSVStatus = 'failed';
        });
    }
});

export const { resetExportApplicantsAsCSV, resetIncompleteApplicants } = incompleteApplicantsSlice.actions;
export default incompleteApplicantsSlice;
